import React, { useEffect, useRef, useState } from "react";
import { Button, Col, Empty, Row, Space, Spin, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../components/PageHeader";
import {
  useGetOdReviewCoursesQuery,
  useGetOdReviewRunQuery,
} from "../../../store/api/odReviewApi";
import { useOdReviewQueue } from "./useOdReviewQueue";
import IssueQueue from "./IssueQueue";
import IssueCard from "./IssueCard";
import ActivityUnderReview, {
  ActivityUnderReviewHandle,
} from "./ActivityUnderReview";
import RunCounters from "./RunCounters";

const { Text } = Typography;

const POLL_MS = 5000;

const ReviewRunView: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const runIdNum = Number(runId);
  const isValidRunId = runId !== undefined && !Number.isNaN(runIdNum);
  const activityRef = useRef<ActivityUnderReviewHandle>(null);

  const [runPollMs, setRunPollMs] = useState<number>(POLL_MS);
  const { data: run } = useGetOdReviewRunQuery(runIdNum, {
    skip: !isValidRunId,
    pollingInterval: runPollMs,
  });

  useEffect(() => {
    if (run && (run.status === "COMPLETED" || run.status === "FAILED")) {
      setRunPollMs(0);
    }
  }, [run?.status]);

  const runIsActive =
    !run || run.status === "PENDING" || run.status === "IN_PROGRESS";
  const queue = useOdReviewQueue(runIdNum, runIsActive ? POLL_MS : undefined);
  const { data: courseOptions = [] } = useGetOdReviewCoursesQuery({});

  const breadcrumb = (() => {
    if (!queue.currentIssue) return "Revisión de ODs";
    const course = courseOptions.find(
      (c) => c.course_id === queue.currentIssue!.course_id,
    );
    if (!course) return "Revisión de ODs";
    const weeks = Array.from(
      new Set(course.programs.map((p) => `Semana ${p.week}`)),
    );
    return [course.course_title, ...weeks].join(" › ");
  })();

  const handlePrimaryAction = async () => {
    const issue = queue.currentIssue;
    if (!issue) return;
    if (issue.status === "PENDING_MANUAL") {
      await queue.resolveManual();
      return;
    }
    if (issue.error_type === "FOTO_INCORRECTA") {
      await queue.markManual();
      return;
    }
    const appliedValue = activityRef.current?.getAppliedValue() ?? "";
    const success = await queue.accept(appliedValue);
    if (success) {
      activityRef.current?.confirmAccepted(issue, appliedValue);
    }
  };

  if (!isValidRunId) {
    return (
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <PageHeader title="Revisión de ODs" />
        <Empty description="Corrida inválida." />
      </Space>
    );
  }

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <PageHeader
        title={breadcrumb}
        extra={
          <Space size="middle">
            {queue.currentIssue && (
              <Space>
                <Button
                  icon={<LeftOutlined />}
                  disabled={!queue.hasPrev}
                  onClick={queue.goToPrev}
                />
                <Text>{`${queue.currentIndex + 1} de ${queue.total}`}</Text>
                <Button
                  icon={<RightOutlined />}
                  disabled={!queue.hasNext}
                  onClick={queue.goToNext}
                />
              </Space>
            )}
            <Button onClick={() => navigate("/revision-ods")}>
              Salir de la revisión
            </Button>
          </Space>
        }
      />

      {run && <RunCounters run={run} inline />}

      {queue.isLoading ? (
        <div
          style={{ display: "flex", justifyContent: "center", padding: 48 }}
        >
          <Spin size="large" tip="Cargando errores..." />
        </div>
      ) : (
        <Row gutter={24}>
          <Col span={7}>
            <IssueQueue
              issues={queue.issues}
              selectedIssueId={queue.currentIssue?.id ?? null}
              onSelect={queue.selectIssue}
              errorTypeCounts={queue.errorTypeCounts}
              hiddenErrorTypes={queue.hiddenErrorTypes}
              onToggleErrorType={queue.toggleErrorType}
            />
          </Col>
          <Col span={17}>
            {queue.currentIssue ? (
              <Space
                direction="vertical"
                style={{ width: "100%" }}
                size="middle"
              >
                <IssueCard
                  issue={queue.currentIssue}
                  onAccept={handlePrimaryAction}
                  onResolve={handlePrimaryAction}
                  onReject={queue.reject}
                />
                <ActivityUnderReview
                  ref={activityRef}
                  issue={queue.currentIssue}
                />
              </Space>
            ) : (
              <Empty
                description={
                  queue.hiddenIssueCount > 0
                    ? `No quedan errores pendientes con los filtros activos. Hay ${queue.hiddenIssueCount} errores ocultos.`
                    : "No quedan errores pendientes en esta corrida."
                }
              />
            )}
          </Col>
        </Row>
      )}
    </Space>
  );
};

export default ReviewRunView;
